/**
 * Fixed-threshold coloring for numeric metric cells — the counterpart to `TrendBadge`'s
 * percentile cutoffs. A metric declares an ordered list of bands; each band owns the values at
 * or below its `max`, and the resolver returns the first band a value falls into. Directionality
 * (higher-is-better vs. lower-is-better) is expressed purely by which `status` each band carries,
 * so the same shape colors "% logged in" (high = good) and "overtime %" (high = bad) alike.
 */
export enum MetricStatus {
	Critical = "critical",
	Warning = "warning",
	Good = "good",
	Neutral = "neutral",
}

export interface MetricThresholdBand {
	/**
	 * Inclusive upper bound for this band: a value `<= max` falls in it. Bands are evaluated in
	 * order, so list them ascending. The final band uses `null` as a catch-all for everything above
	 * the last bound.
	 */
	max: number | null;
	status: MetricStatus;
}

export type MetricThresholds = readonly MetricThresholdBand[];

/**
 * Resolve a value to its band's status. Missing or non-finite values (`NaN`, `±Infinity`) are
 * `neutral` (no rule applies), as is a value that falls past every band when the list has no `null`
 * catch-all — a misconfiguration we surface as neutral rather than silently coloring it.
 */
export const resolveMetricStatus = (value: number | null | undefined, bands: MetricThresholds): MetricStatus => {
	if (typeof value !== "number" || !Number.isFinite(value)) return MetricStatus.Neutral;
	for (const band of bands) {
		if (band.max === null || value <= band.max) return band.status;
	}
	return MetricStatus.Neutral;
};

/**
 * Traffic-light bands for a 0–100 percentage where higher is better: `<= 50` critical (red),
 * `50–70` warning (orange), `> 70` good (green). Shared so every "% healthy" column reads on the
 * same scale rather than re-deriving cutoffs per column.
 */
export const PERCENT_HIGHER_IS_BETTER_THRESHOLDS: MetricThresholds = [
	{ max: 50, status: MetricStatus.Critical },
	{ max: 70, status: MetricStatus.Warning },
	{ max: null, status: MetricStatus.Good },
];

/**
 * Bands for a value measured as a percentage of a reference (`value / reference * 100`), where
 * exceeding the reference is worse: at/under the reference and up to +5% is good, +5–10% is a
 * warning, over +10% is critical. Used to color Avg. Actual HPPD against its target.
 */
export const OVER_REFERENCE_THRESHOLDS: MetricThresholds = [
	{ max: 105, status: MetricStatus.Good },
	{ max: 110, status: MetricStatus.Warning },
	{ max: null, status: MetricStatus.Critical },
];

/**
 * Resolve a status by how far `value` sits relative to a `reference`, expressed as a percentage
 * (`value / reference * 100`) and matched against `bands`. For coloring one column against another
 * (e.g. actual vs. target). Non-finite inputs or a non-positive reference are `neutral` — no rule
 * applies rather than a misleading color.
 */
export const resolveStatusAgainstReference = (
	value: number | null | undefined,
	reference: number | null | undefined,
	bands: MetricThresholds,
): MetricStatus => {
	if (typeof value !== "number" || !Number.isFinite(value)) return MetricStatus.Neutral;
	if (typeof reference !== "number" || !Number.isFinite(reference) || reference <= 0) return MetricStatus.Neutral;
	return resolveMetricStatus((value / reference) * 100, bands);
};
